<script setup>
import {computed, ref} from "vue";
import generateId from "@/composables/generateId";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },
  label: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: ""
  },
  alert: {
    type: String,
    default: ""
  },
  error: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["small", "medium", "large"].includes(value)
  },
  height: {
    type: [String, Number],
    default: null,
    validator: (value) => {
      if (value === null) return true;
      if (typeof value === "number") return value >= 0;
      return /^(auto|\d+(px|rem|em|%|vh|vw))$/.test(value);
    }
  },
  autofocus: {
    type: Boolean,
    default: false
  }
});

const computedHeight = computed(() => {
  if (!props.height) return null;
  if (typeof props.height === "number") {
    return `${props.height}px`;
  }
  return props.height;
});
const inputId = generateId();

const emit = defineEmits(["update:modelValue", "focus", "blur", "input", "change", "suffix-click"]);

const isFocused = ref(false);
const inputRef = ref(null);

const inputWrapperClasses = computed(() => [
  `input-wrapper--${props.size}`,
  {
    "input-wrapper--focused": isFocused.value,
    "input-wrapper--disabled": props.disabled,
    "input-wrapper--error": props.error
  }
]);

const handleFocus = (event) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleBlur = (event) => {
  isFocused.value = false;
  emit("blur", event);
};

const localModelValue = computed(({
  set(value) {
    emit("update:modelValue", value);
  },
  get() {
    return props.modelValue;
  }
}));

const focus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({
  focus
});
</script>

<template>
  <div class="input-container">
    <label
      v-if="props.label"
      :for="inputId"
      class="input-container__label"
    >
      {{ props.label }}
      <span
        v-if="required"
        class="input-container__label--required"
      >*</span>
    </label>
    <div
      :class="inputWrapperClasses"
      :style="{ height: computedHeight }"
      class="input-wrapper"
    >
      <textarea
        ref="inputRef"
        v-model="localModelValue"
        class="input-wrapper__input scroll"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :autofocus="autofocus"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>
    <div
      v-if="props.alert"
      class="input-container__alert"
      :class="{'input-container__alert--error': props.error}"
    >
      {{ props.alert }}
    </div>
  </div>
</template>

<style scoped lang="scss">

.input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__label {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--form-primary-title-text);

    &--required { color: var(--system-red); }
  }

  &__alert {
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    color: var(--form-primary-placeholder-text);

    &--error {
      color: var(--system-red);
    }
  }
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 2px;
  background-color: var(--form-primary-default);
  border: 1px solid var(--form-primary-bd-default);
  padding: 12px 10px 12px 12px;
  border-radius: 8px;
  color: var(--form-primary-title-text);

  &__prefix {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:hover:not(.input-wrapper--focused):not(.input-wrapper--disabled) {
    background-color: var(--form-primary-hover);
    border-color: var(--form-primary-bd-hover);
  }

  &--focused {
    background-color: var(--form-primary-typing);
    border-color: var(--form-primary-bd-typing);
  }

  &--error {
    border-color: var(--border-bd-error);
    background-color: var(--form-primary-default);
  }

  &--disabled {
    background-color: var(--form-primary-disabled);
    border-color: var(--border-bd-disabled);
    color: var(--text-text-disabled);
  }

  &__input {
    all: unset;
    width: 100%;
    height: 100%;
    word-wrap: break-word;

    &:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 1000px var(--form-primary-default) inset !important;
      -webkit-text-fill-color: var(--form-primary-title-text) !important;
      background-color: var(--form-primary-default) !important;
    }

    &:-webkit-autofill:hover {
      -webkit-box-shadow: 0 0 0 1000px var(--form-primary-hover) inset !important;
      -webkit-text-fill-color: var(--form-primary-title-text) !important;
      background-color: var(--form-primary-hover) !important;
    }

    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px var(--form-primary-typing) inset !important;
      -webkit-text-fill-color: var(--form-primary-title-text) !important;
      background-color: var(--form-primary-typing) !important;
    }

    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px var(--form-primary-typing) inset !important;
      -webkit-text-fill-color: var(--form-primary-title-text) !important;
      background-color: var(--form-primary-typing) !important;
    }
  }

  &:hover:not(.input-wrapper--focused):not(.input-wrapper--disabled) &__input:-webkit-autofill,
  &:hover:not(.input-wrapper--focused):not(.input-wrapper--disabled) &__input:-webkit-autofill:hover {
    -webkit-box-shadow: 0 0 0 1000px var(--form-primary-hover) inset !important;
    -webkit-text-fill-color: var(--form-primary-title-text) !important;
    background-color: var(--form-primary-hover) !important;
  }

  &--focused &__input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px var(--form-primary-typing) inset !important;
    -webkit-text-fill-color: var(--form-primary-title-text) !important;
    background-color: var(--form-primary-typing) !important;
  }

  // Размеры
  &--small { height: 30px; }

  &--medium { height: 36px; }

  &--large { height: 42px; }

}

// Стили для скрола
.scroll {
  overflow-y: auto;
  padding-right: 2px;

  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: #1A64D433 transparent;
  }

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #1A64D433;
    border-radius: 30px;
  }
}

</style>